import React, { useState, useEffect, useRef } from "react";
import {
  DateTimePicker,
  DatePicker,
  TimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

const loadFonts = () => {
  const link = document.createElement("link");
  link.href =
    "https://fonts.googleapis.com/css2?family=Amita&family=Bad+Script&family=Damion&family=Dancing+Script&family=Felipa&family=Julee&family=Kaushan+Script&family=Lugrasimo&family=Pacifico&family=Sofia&family=Yellowtail&display=swap";
  link.rel = "stylesheet";
  document.head.appendChild(link);
};

const STATIC_TYPES = ["text_only", "rich_text", "notice_text", "warning_text"];

const getQuestionColClass = (question, group) => {
  if (!question) return "col-sm-12";
  if (question.answerType === "label" || STATIC_TYPES.includes(question.answerType))
    return "col-sm-12";
  if (group && group.column_layout === "double_column") return "col-sm-6";
  return "col-sm-12";
};

const smoothScrollModal = (modalEl, targetY, duration = 650) => {
  const startY = modalEl.scrollTop;
  const diff = targetY - startY;
  if (Math.abs(diff) < 2) return;
  const ease = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  let startTime = null;
  const tick = (now) => {
    if (!startTime) startTime = now;
    const progress = Math.min((now - startTime) / duration, 1);
    modalEl.scrollTo(0, startY + diff * ease(progress));
    if (progress < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};

const FormRenderer = ({ formData, formTitle, formDesc, formGroups = [] }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const [mobileNavVisible, setMobileNavVisible] = useState(false);
  const sectionRefs = useRef([]);
  const heroRef = useRef(null);
  const isScrollingRef = useRef(false);
  const mobileNavTimerRef = useRef(null);

  useEffect(() => {
    loadFonts();
  }, []);

  // Track active tab while scrolling inside modal — suppressed during programmatic scroll
  useEffect(() => {
    if (!formGroups || formGroups.length <= 1) return;
    const modalEl = document.getElementById("modalPreviewForm");
    const obs = [];
    sectionRefs.current.forEach((el, idx) => {
      if (!el) return;
      const o = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isScrollingRef.current)
            setActiveTabIndex(idx);
        },
        {
          threshold: 0.25,
          root: modalEl || null,
          rootMargin: "-40px 0px -50% 0px",
        }
      );
      o.observe(el);
      obs.push(o);
    });
    return () => obs.forEach((o) => o.disconnect());
  }, [formGroups]);

  // Show floating nav when hero card scrolls out of view in modal
  useEffect(() => {
    if (!formGroups || formGroups.length <= 1) return;
    const el = heroRef.current;
    if (!el) return;
    const modalEl = document.getElementById("modalPreviewForm");
    const obs = new IntersectionObserver(
      ([entry]) => setShowFloatingNav(!entry.isIntersecting),
      { threshold: 0, root: modalEl || null }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [formGroups]);

  // Mobile: flash floating nav on modal scroll, auto-hide after 2s
  useEffect(() => {
    if (!formGroups || formGroups.length <= 1) return;
    const modalEl = document.getElementById("modalPreviewForm");
    if (!modalEl) return;
    const show = () => {
      if (!showFloatingNav) return;
      setMobileNavVisible(true);
      if (mobileNavTimerRef.current) clearTimeout(mobileNavTimerRef.current);
      mobileNavTimerRef.current = setTimeout(
        () => setMobileNavVisible(false),
        2000
      );
    };
    modalEl.addEventListener("scroll", show, { passive: true });
    return () => {
      modalEl.removeEventListener("scroll", show);
      if (mobileNavTimerRef.current) clearTimeout(mobileNavTimerRef.current);
    };
  }, [formGroups, showFloatingNav]);

  const scrollToSection = (index) => {
    setActiveTabIndex(index);
    const el = sectionRefs.current[index];
    if (!el) return;
    isScrollingRef.current = true;
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 750);
    const modalEl = document.getElementById("modalPreviewForm");
    if (modalEl) {
      const modalRect = modalEl.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const targetY = modalEl.scrollTop + elRect.top - modalRect.top - 80;
      smoothScrollModal(modalEl, targetY);
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <div
        className="form-renderer-container"
        style={{ background: "#eef1f7", minHeight: "100%" }}
      >
        {/* ── Scoped styles — mirrors client SP layout styles ── */}
        <style>{`
          .obs-preview-hero-tabs > div::-webkit-scrollbar { display: none; }

          .form-renderer-container .form-label {
            font-size: 12.5px !important;
            font-weight: 600 !important;
            color: #526484 !important;
            margin-bottom: 5px !important;
            letter-spacing: 0.01em;
          }
          .form-renderer-container .form-label.obs-section-label {
            font-size: 13.5px !important;
            font-weight: 700 !important;
            color: #344357 !important;
            margin-bottom: 4px !important;
          }
          .form-renderer-container .obs-tab-btn:focus { outline: none; }

          @media (max-width: 768px) {
            .obs-preview-hero-tabs { display: none !important; }
            .obs-preview-floating-nav {
              opacity: 0;
              pointer-events: none;
              transition: opacity 0.35s ease !important;
              right: 8px !important;
              padding: 5px 4px !important;
              gap: 3px !important;
              border-radius: 20px !important;
            }
            .obs-preview-floating-nav.obs-mobile-visible {
              opacity: 1;
              pointer-events: auto;
            }
            .obs-preview-pill-btn {
              width: 18px !important;
              height: 18px !important;
              font-size: 8px !important;
            }
          }
          @media (min-width: 769px) and (max-width: 1024px) {
            .obs-preview-hero-tabs .obs-tab-btn { padding: 12px 10px !important; }
            .obs-preview-hero-tabs .obs-tab-label { font-size: 11px !important; }
            .obs-preview-hero-tabs .obs-tab-circle {
              width: 22px !important;
              height: 22px !important;
              min-width: 22px !important;
              font-size: 10px !important;
            }
            .obs-preview-hero-tabs > div { padding: 0 12px !important; }
          }

          .obs-preview-tooltip-wrapper { display: inline-block; position: relative; }
          .obs-preview-tooltip-wrapper .obs-preview-tooltip-box {
            display: none;
            position: absolute;
            background: #364a63;
            color: #fff;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            white-space: nowrap;
            z-index: 9999;
            top: 22px;
            left: 0;
            min-width: 150px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          }
          .obs-preview-tooltip-wrapper:hover .obs-preview-tooltip-box { display: block; }
        `}</style>

        {/* ── Hero card: title + description + group tabs ── */}
        <div
          ref={heroRef}
          style={{
            background: "#F5F6FA",
            borderRadius: "16px",
            marginBottom: "1.5rem",
            overflow: "hidden",
            border: "1px solid #dce2ec",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          }}
        >
          <div style={{ padding: "36px 48px 28px", textAlign: "center" }}>
            <h2
              style={{
                margin: "0 0 10px",
                fontSize: "1.85rem",
                fontWeight: 700,
                color: "#1c2b46",
                lineHeight: 1.25,
                letterSpacing: "-0.02em",
              }}
            >
              {formTitle || "Untitled Form"}
            </h2>
            {formDesc && (
              <p
                style={{
                  margin: "0 auto",
                  fontSize: "15px",
                  color: "#526484",
                  lineHeight: 1.7,
                  maxWidth: "600px",
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: formDesc }} />
              </p>
            )}
          </div>

          {/* Group navigation tabs */}
          {formGroups && formGroups.length > 1 && (
            <div
              className="obs-preview-hero-tabs"
              style={{ borderTop: "1px solid #dce2ec" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  overflowX: "auto",
                  padding: "0 24px",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {formGroups.map((group, index) => {
                  const isActive = activeTabIndex === index;
                  return (
                    <button
                      key={group.id || index}
                      onClick={() => scrollToSection(index)}
                      type="button"
                      className="obs-tab-btn"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "9px",
                        padding: "14px 16px",
                        border: "none",
                        borderBottom: isActive
                          ? "3px solid #1ee0ac"
                          : "3px solid transparent",
                        background: "transparent",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        transition: "border-color 0.2s ease",
                        outline: "none",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        className="obs-tab-circle"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "24px",
                          height: "24px",
                          minWidth: "24px",
                          borderRadius: "50%",
                          background: isActive ? "#1ee0ac" : "#e4e9f2",
                          color: isActive ? "#0d1b38" : "#6b7c95",
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        {index + 1}
                      </span>
                      <span
                        className="obs-tab-label"
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          letterSpacing: "0.01em",
                          color: isActive ? "#0d1b38" : "#6b7c95",
                        }}
                      >
                        {group.group_title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Floating vertical step pill (mirrors client) ── */}
        {formGroups && formGroups.length > 1 && (
          <div
            className={`obs-preview-floating-nav${mobileNavVisible ? " obs-mobile-visible" : ""}`}
            style={{
              display: showFloatingNav ? "flex" : "none",
              position: "fixed",
              right: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1060,
              background: "#fff",
              borderRadius: "30px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
              padding: "7px 5px",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
              transition: "opacity 0.35s ease",
            }}
          >
            {formGroups.map((group, index) => {
              const isActive = activeTabIndex === index;
              return (
                <button
                  key={group.id || index}
                  onClick={() => scrollToSection(index)}
                  title={group.group_title}
                  className="obs-preview-pill-btn"
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    background: isActive ? "#0d1b38" : "#e4e9f2",
                    border: isActive ? "none" : "1px solid #c8d3df",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isActive ? "#fff" : "#6b7c95",
                    fontSize: "9px",
                    fontWeight: 700,
                    transition: "all 0.25s ease",
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Admin preview badge ── */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "12px",
          }}
        >
          <span
            style={{
              background: "#f0f3ff",
              color: "#6576ff",
              border: "1px solid #c4d0ff",
              borderRadius: "6px",
              padding: "4px 12px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            <em className="icon ni ni-eye" style={{ marginRight: "4px" }}></em>
            Admin Preview — Read Only
          </span>
        </div>

        {/* ── Group sections ── */}
        {formGroups.map((group, groupIndex) => (
          <div
            key={group.id || groupIndex}
            ref={(el) => (sectionRefs.current[groupIndex] = el)}
            className="mb-4"
            style={{
              scrollMarginTop: "80px",
              background: "#fff",
              borderRadius: "14px",
              boxShadow: "0 1px 12px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            {/* Group header */}
            <div
              style={{
                borderBottom: "1px solid #e5e9f2",
                padding: "20px 24px 18px",
                background: "#fff",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <h6
                  style={{
                    margin: "0 0 3px",
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#1c2b46",
                    letterSpacing: "0",
                  }}
                >
                  Step {groupIndex + 1} &mdash; {group.group_title}
                </h6>
                {group.group_desc && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      color: "#8094ae",
                      lineHeight: 1.55,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: group.group_desc.replace(/\n/g, "<br />"),
                    }}
                  />
                )}
              </div>
            </div>

            {/* Group fields */}
            <div className="card-inner">
              <div className="row g-gs">
                {group.questions
                  .map((questionId) =>
                    formData[0]?.questions.find((q) => q.id === questionId)
                  )
                  .filter(Boolean)
                  .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
                  .map((question) => {
                    const colClass = getQuestionColClass(question, group);
                    return (
                      <div
                        key={question.id}
                        className={`question-container ${colClass}`}
                      >
                        {/* Label / question text */}
                        <div className="form-group question-text mb-0">
                          {question.answerType === "label" ? (
                            <label className="form-label obs-section-label">
                              {question.questionText}{" "}
                              {question.required && (
                                <sup className="text-danger">*</sup>
                              )}
                              {question.tooltip &&
                                question.tooltip.trim() !== "" && (
                                  <div className="obs-preview-tooltip-wrapper">
                                    <em
                                      className="icon ni ni-info"
                                      style={{
                                        color: "#6576ff",
                                        marginLeft: "5px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                      }}
                                    ></em>
                                    <div className="obs-preview-tooltip-box">
                                      <p>{question.tooltip}</p>
                                    </div>
                                  </div>
                                )}
                            </label>
                          ) : !STATIC_TYPES.includes(question.answerType) ? (
                            <label className="form-label">
                              {question.questionText}{" "}
                              {question.required && (
                                <sup className="text-danger">*</sup>
                              )}
                              {question.tooltip &&
                                question.tooltip.trim() !== "" && (
                                  <div className="obs-preview-tooltip-wrapper">
                                    <em
                                      className="icon ni ni-info"
                                      style={{
                                        color: "#6576ff",
                                        marginLeft: "5px",
                                        cursor: "pointer",
                                        fontSize: "16px",
                                      }}
                                    ></em>
                                    <div className="obs-preview-tooltip-box">
                                      <p>{question.tooltip}</p>
                                    </div>
                                  </div>
                                )}
                            </label>
                          ) : null}

                          {/* text_only */}
                          {question.answerType === "text_only" &&
                            (question.textType === "oli" ? (
                              <ol
                                style={{
                                  paddingLeft: "20px",
                                  marginLeft: "20px",
                                }}
                              >
                                {(question.textOnly || "")
                                  .split("\n")
                                  .filter((l) => l.trim())
                                  .map((line, li) => (
                                    <li
                                      key={li}
                                      style={{
                                        listStyleType: "decimal",
                                        paddingLeft: "10px",
                                      }}
                                    >
                                      {line}
                                    </li>
                                  ))}
                              </ol>
                            ) : question.textType === "sli" ? (
                              <ul
                                style={{
                                  paddingLeft: "20px",
                                  marginLeft: "20px",
                                }}
                              >
                                {(question.textOnly || "")
                                  .split("\n")
                                  .filter((l) => l.trim())
                                  .map((line, li) => (
                                    <li
                                      key={li}
                                      style={{
                                        listStyleType: "disc",
                                        paddingLeft: "10px",
                                      }}
                                    >
                                      {line}
                                    </li>
                                  ))}
                              </ul>
                            ) : (
                              <p>{question.textOnly}</p>
                            ))}

                          {/* rich_text */}
                          {question.answerType === "rich_text" && (
                            <div
                              style={{
                                fontSize: "14px",
                                lineHeight: 1.65,
                                color: "#526484",
                              }}
                              dangerouslySetInnerHTML={{
                                __html: question.textOnly || "",
                              }}
                            />
                          )}

                          {/* notice_text */}
                          {question.answerType === "notice_text" && (
                            <div
                              style={{
                                display: "flex",
                                gap: "12px",
                                alignItems: "flex-start",
                                background: "#eef3ff",
                                border: "1px solid #c4d0ff",
                                borderLeft: "4px solid #6576ff",
                                borderRadius: "6px",
                                padding: "14px 16px",
                                marginBottom: "4px",
                              }}
                            >
                              <em
                                className="icon ni ni-info-fill"
                                style={{
                                  color: "#6576ff",
                                  fontSize: "18px",
                                  marginTop: "1px",
                                  flexShrink: 0,
                                }}
                              ></em>
                              <div
                                style={{
                                  fontSize: "13.5px",
                                  color: "#364a63",
                                  lineHeight: 1.6,
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: question.textOnly || "",
                                }}
                              />
                            </div>
                          )}

                          {/* warning_text */}
                          {question.answerType === "warning_text" && (
                            <div
                              style={{
                                display: "flex",
                                gap: "12px",
                                alignItems: "flex-start",
                                background: "#fff8ea",
                                border: "1px solid #ffd591",
                                borderLeft: "4px solid #f4a600",
                                borderRadius: "6px",
                                padding: "14px 16px",
                                marginBottom: "4px",
                              }}
                            >
                              <em
                                className="icon ni ni-alert-fill"
                                style={{
                                  color: "#f4a600",
                                  fontSize: "18px",
                                  marginTop: "1px",
                                  flexShrink: 0,
                                }}
                              ></em>
                              <div
                                style={{
                                  fontSize: "13.5px",
                                  color: "#364a63",
                                  lineHeight: 1.6,
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: question.textOnly || "",
                                }}
                              />
                            </div>
                          )}

                          {/* Attachment download badge */}
                          {question.imageUrl &&
                            !STATIC_TYPES.includes(question.answerType) && (
                              <a style={{ float: "right", cursor: "default" }}>
                                <span className="badge badge-outline-primary">
                                  <em className="icon ni ni-download"></em>{" "}
                                  Download File
                                </span>
                              </a>
                            )}
                        </div>

                        {/* Admin-uploaded question image (mirrors client) */}
                        {question.imageUrl &&
                          question.answerType !== "image_input" &&
                          !STATIC_TYPES.includes(question.answerType) && (
                            <img
                              src={question.imageUrl}
                              alt="Admin Uploaded"
                              className="question-image"
                            />
                          )}

                        {/* ── Field inputs (all disabled / read-only in preview) ── */}

                        {question.answerType === "input" && (
                          <div className="form-group mb-0">
                            <div className="form-control-wrap">
                              <input
                                className="form-control form-control-lg"
                                value=""
                                type="text"
                                disabled
                                readOnly
                                maxLength={question.maxLength || 100}
                                autoComplete="off"
                              />
                            </div>
                            {question.maxLength && (
                              <small
                                className="text-muted"
                                style={{ display: "block", marginTop: "4px" }}
                              >
                                • Maximum length is {question.maxLength}{" "}
                                characters.
                              </small>
                            )}
                          </div>
                        )}

                        {question.answerType === "fixed" && (
                          <div className="form-group mb-0">
                            <div className="form-control-wrap">
                              <input
                                className="form-control form-control-lg"
                                value={question.answer || ""}
                                type="text"
                                disabled
                                readOnly
                                maxLength={255}
                                autoComplete="off"
                              />
                            </div>
                          </div>
                        )}

                        {question.answerType === "label" && (
                          <div className="form-group mb-0">
                            <h6></h6>
                          </div>
                        )}

                        {question.answerType === "textarea" && (
                          <div className="form-group">
                            <div className="form-control-wrap">
                              <textarea
                                className="form-control form-control-lg"
                                value=""
                                disabled
                                readOnly
                                maxLength={question.maxLength || 500}
                              />
                            </div>
                          </div>
                        )}

                        {question.answerType === "datetime" && (
                          <div className="form-group">
                            <DateTimePicker
                              variant="inline"
                              inputVariant="outlined"
                              fullWidth
                              value={null}
                              disabled
                            />
                          </div>
                        )}

                        {question.answerType === "date" && (
                          <div className="form-group">
                            <DatePicker
                              variant="inline"
                              inputVariant="outlined"
                              fullWidth
                              value={null}
                              disabled
                            />
                          </div>
                        )}

                        {question.answerType === "time" && (
                          <div className="form-group">
                            <TimePicker
                              variant="inline"
                              inputVariant="outlined"
                              fullWidth
                              value={null}
                              disabled
                            />
                          </div>
                        )}

                        {question.answerType === "dropdown" && (
                          <div className="form-group">
                            <div className="form-control-wrap">
                              <div className="form-control-select">
                                <select
                                  className="form-control form-control-lg"
                                  value=""
                                  disabled
                                >
                                  <option value="">Select an option</option>
                                  {question.options.map((option, oi) => (
                                    <option key={oi} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Checkbox — full client hover style */}
                        {question.answerType === "checkbox" && (
                          <div style={{ marginTop: "8px" }}>
                            {question.options.map((option, oi) => {
                              const hKey = `prev_cb_${question.id}_${oi}`;
                              const isHovered = hoveredId === hKey;
                              return (
                                <label
                                  key={oi}
                                  htmlFor={hKey}
                                  onMouseEnter={() => setHoveredId(hKey)}
                                  onMouseLeave={() => setHoveredId(null)}
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "14px",
                                    padding: "14px 16px",
                                    borderRadius: "8px",
                                    border: isHovered
                                      ? "1.5px solid #1ee0ac"
                                      : "1.5px solid #e5e9f2",
                                    background: isHovered
                                      ? "#edfaf6"
                                      : "#ffffff",
                                    cursor: "default",
                                    transition: "all 0.15s ease",
                                    marginBottom: "10px",
                                    width: "100%",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    className="vdrSelected"
                                    id={hKey}
                                    checked={false}
                                    readOnly
                                    onChange={() => {}}
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                      flexShrink: 0,
                                      marginTop: "1px",
                                      accentColor: "#1ee0ac",
                                      cursor: "default",
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "13.5px",
                                      color: isHovered ? "#1c2b46" : "#526484",
                                      fontWeight: isHovered ? 600 : 400,
                                      lineHeight: 1.55,
                                      flex: 1,
                                    }}
                                  >
                                    {option}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}

                        {/* Radio — full client hover style */}
                        {question.answerType === "radio" && (
                          <div style={{ marginTop: "8px" }}>
                            {question.options.map((option, oi) => {
                              const hKey = `prev_radio_${question.id}_${oi}`;
                              const isHovered = hoveredId === hKey;
                              return (
                                <label
                                  key={oi}
                                  htmlFor={hKey}
                                  onMouseEnter={() => setHoveredId(hKey)}
                                  onMouseLeave={() => setHoveredId(null)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "14px",
                                    padding: "14px 16px",
                                    borderRadius: "8px",
                                    border: isHovered
                                      ? "1.5px solid #1ee0ac"
                                      : "1.5px solid #e5e9f2",
                                    background: isHovered
                                      ? "#edfaf6"
                                      : "#ffffff",
                                    cursor: "default",
                                    transition: "all 0.15s ease",
                                    marginBottom: "10px",
                                    width: "100%",
                                  }}
                                >
                                  <input
                                    type="radio"
                                    id={hKey}
                                    name={`prev_radio_${question.id}`}
                                    value={option}
                                    readOnly
                                    onChange={() => {}}
                                    style={{ display: "none" }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "13.5px",
                                      color: isHovered ? "#1c2b46" : "#526484",
                                      fontWeight: isHovered ? 600 : 400,
                                      lineHeight: 1.55,
                                      flex: 1,
                                    }}
                                  >
                                    {option}
                                  </span>
                                  <span
                                    style={{
                                      width: "24px",
                                      height: "24px",
                                      borderRadius: "50%",
                                      background: "transparent",
                                      border: "2px solid #d0d5dd",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      transition: "all 0.15s ease",
                                    }}
                                  ></span>
                                </label>
                              );
                            })}
                          </div>
                        )}

                        {question.answerType === "image_input" && (
                          <div className="form-group">
                            <div
                              className="form-control-group"
                              id="fileContainer"
                            >
                              <input
                                type="file"
                                className="file-block"
                                name="add_picture"
                                disabled
                                accept=".png, .jpg, .jpeg, .pdf, .doc, .docx, .xlx, .xlxs, .csv"
                                autoComplete="new-password"
                              />
                            </div>
                          </div>
                        )}

                        {/* Signature — dashed placeholder (no canvas in preview) */}
                        {question.answerType === "signature" && (
                          <div
                            style={{
                              border: "1.5px dashed #d0d5dd",
                              borderRadius: "8px",
                              padding: "28px 24px",
                              textAlign: "center",
                              color: "#8094ae",
                              fontSize: "13.5px",
                              background: "#fafbfc",
                            }}
                          >
                            <em
                              className="icon ni ni-pen"
                              style={{
                                fontSize: "22px",
                                display: "block",
                                marginBottom: "6px",
                                color: "#b0bec9",
                              }}
                            ></em>
                            Signature field — Preview only
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        ))}

        {/* ── Footer: preview-only notice (matches client "submission disabled" style) ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "12px",
            padding: "18px 0 4px",
            marginTop: "8px",
            borderTop: "1px solid #e5e9f2",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 16px",
              borderRadius: "6px",
              background: "#fff8ea",
              border: "1px solid #ffd591",
              color: "#8a6c00",
              fontSize: "13.5px",
              fontWeight: 500,
            }}
          >
            <em className="icon ni ni-lock" style={{ fontSize: "16px" }}></em>
            Admin Preview &mdash; Submission disabled
          </div>
        </div>
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default FormRenderer;
